import { uniq } from "lodash";
import { getRoot, Instance, SnapshotOut, types } from "mobx-state-tree";

import { UserRole } from "../../types";
import { withEnvironment } from "../extensions/withEnvironment";
import { RootStore } from "../RootStore/RootStore";
import { UserModel, UserSnapshot } from "../User/User";

export const PROMOCODE_KEY = "jetclass_promocode";

export interface LoginProps {
  email: string;
  password: string;
}

export const ALL_REGISTER_VALUES = [
  "firstName",
  "lastName",
  "email",
  "password",
  "phone",
  "gender",
  "birthday",
  "profileCountryId",
  "profileCityId",
  "profileDegreeId",
] as const;

export type RegisterValue = (typeof ALL_REGISTER_VALUES)[number];

export type RegisterProps = {
  [key in RegisterValue]?: string;
};

export const isRegisterValue = (value: string): value is RegisterValue =>
  ALL_REGISTER_VALUES.includes(value as RegisterValue);

export interface RestorePasswordProps {
  email: string;
}

export interface ChangePasswordProps {
  password: string;
  passwordRepeat: string;
}

export interface RegisterSecondAnateleProps {
  occupationId?: string;
  occupationStr?: string;
  russiaRegionId?: string;
  cityStr?: string;
  russiaDistrictId?: string;
}

export interface RegisterSecondGovoriruProps {
  cityStr: string;
}

export interface UpdateUserProps {
  phone: string;
  profileDegreeId: string;
  profileCountryId?: string;
  profileCityId?: string;
}

export const AuthStoreModel = types
  .model("AuthStore")
  .props({
    user: types.maybeNull(UserModel),
    isPasswordSetupRequired: types.maybeNull(types.boolean),
    entranceTestId: types.maybeNull(types.string),
    dontShowExercisesResults: types.maybeNull(types.boolean),
    userError: types.optional(types.boolean, false),
  })
  .extend(withEnvironment)
  .volatile((self) => ({
    _onUnauthorizedError: (() => {}) as () => void,
  }))
  .actions((self) => ({
    saveUser: (userSnapshot: UserSnapshot) => {
      self.user = UserModel.create(userSnapshot);
    },
    setUserError(isError: boolean) {
      self.userError = isError;
    },
    setPasswordSetupRequired(isChange: boolean) {
      self.isPasswordSetupRequired = isChange;
    },
    setEntranceTestId(entranceTestId: string | null) {
      self.entranceTestId = entranceTestId;
    },
    setDontShowExercisesResults(value: boolean) {
      self.dontShowExercisesResults = value;
    },
  }))
  .actions((self) => ({
    onUnauthorizedError(fn: () => void) {
      self._onUnauthorizedError = fn;
    },
    getCurrentUser: async () => {
      self.setUserError(false);

      const result = await self.environment.api.getCurrentUser();

      if (result.kind !== "ok") {
        console.error("Error getting current user");

        // Небольшой костыль, по идее все запросы должны делаться через RootStore.fetchApi (там есть логика обработки unauthorized ошибок)
        // Но мы делаем некоторые запросы в сторе где невозможно использовать RootStore.fetchApi но обработать ошибку unauthorized надо
        // Поэтому дублируем код из RootStore.fetchApi
        if (result.kind === "unauthorized") {
          getRoot<RootStore>(self).cleanStore();
          self._onUnauthorizedError();

          return;
        }

        self.setUserError(true);

        return;
      }

      self.setUserError(false);

      const { user } = result;

      self.setPasswordSetupRequired(user.changePassword);

      // Парсинг ролей из API
      // Сначала проверка должна быть по лицензиям
      if (user.licenses?.length) {
        const getResultRolesFromLicenseTypesIds = (): UserRole[] => {
          const licenseTypesIds: string[] = uniq(
            user.licenses.map((license: any) => license.licenseTypeId)
          ).map(String);

          const licenseTypeIdToRole: Record<string, UserRole> = {
            "1": UserRole.STUDENT,
            "2": UserRole.STUDENT,
            "3": UserRole.DISTRIBUTOR,
            "5": UserRole.HR,
            "8": UserRole.COORDINATOR,
          };

          return licenseTypesIds
            .filter((id) => Object.keys(licenseTypeIdToRole).includes(id))
            .map((id) => licenseTypeIdToRole[id]);
        };

        user.roles = getResultRolesFromLicenseTypesIds();

        // Проверка по полю roles только если нет лицензии
      } else if (user.roles?.length) {
        const getResultRolesFromRoles = (): UserRole[] => {
          const roleCodes: string[] = user.roles.map((role: any) => role.code);

          const roleCodeToRole: Record<string, UserRole> = {
            student: UserRole.STUDENT,
            "student-physic": UserRole.STUDENT,
            distributor: UserRole.DISTRIBUTOR,
            admin: UserRole.DISTRIBUTOR,
            coordinator: UserRole.COORDINATOR,
            payroll: UserRole.PAYROLL_ACCOUNTANT,
          };

          return roleCodes
            .filter((code) => Object.keys(roleCodeToRole).includes(code))
            .map((code) => roleCodeToRole[code]);
        };

        user.roles = getResultRolesFromRoles();

        // Если нет ни лицензий ни ролей то это юзер
      } else {
        user.roles = [UserRole.USER];
      }

      // Честно, я без понятия почему но преподаватель приходит так
      if (user.teacherIsActive && !user.roles.includes(UserRole.TEACHER)) {
        user.roles.push(UserRole.TEACHER);
      }

      // Передача пользователя в chatwoot
      if (user?.chatwoot) {
        (window as any)?.$chatwoot?.setUser(
          user.chatwoot.id,
          user.chatwoot.params
        );
      }

      self.saveUser(user);
    },
  }))
  .actions((self) => ({
    login: async (
      email: LoginProps["email"],
      password: LoginProps["password"]
    ) => {
      const result = await self.environment.api.loginUser(email, password);

      if (result.kind === "ok") {
        await self.getCurrentUser();
        (window as any).setChatUserName?.(
          `${self?.user?.firstName} ${self?.user?.lastName}`,
          self?.user?.email
        );
        return true;
      }

      if (result.kind === "unauthorized" || result.kind === "bad-request")
        getRoot<RootStore>(self).addError("Unauthorized");

      return false;
    },
    register: async (props: RegisterProps) => {
      const result = await self.environment.api.registerUser(
        props,
        localStorage.getItem(PROMOCODE_KEY) ?? undefined
      );

      if (result.kind === "ok") {
        await self.getCurrentUser();
        (window as any).setChatUserName?.(
          `${self?.user?.firstName} ${self?.user?.lastName}`,
          self?.user?.email
        );
        localStorage.removeItem(PROMOCODE_KEY);
        return true;
      }

      if (result.kind === "unauthorized")
        getRoot<RootStore>(self).addError("Unauthorized");

      if (result.kind === "bad-data") {
        return result.message;
      }

      return false;
    },
    // TODO Бред что нужно делать два запроса но сейчас бэк не переделать
    updateUser: async (
      props1: RegisterSecondAnateleProps,
      props2: UpdateUserProps
    ) => {
      const result2 = await self.environment.api.updateUser(props2);
      const result1 = await self.environment.api.registerSecondAnatele(props1);

      if (result1.kind === "ok" && result2.kind === "ok") {
        await self.getCurrentUser();

        return true;
      }

      if (result1.kind === "bad-data") {
        return result1.message;
      }

      return false;
    },
    logout: async () => {
      const result = await self.environment.api.logout();

      getRoot<RootStore>(self).cleanStore();

      return result;
    },
    restorePassword: async (email: RestorePasswordProps["email"]) => {
      const result = await self.environment.api.restorePasswordUser(email);

      if (result.kind === "ok") {
        return true;
      }

      return false;
    },
    loginByToken: async (token: string) => {
      const data = await self.environment.api.loginUserByToken(token);
      if (data.kind !== "ok") {
        return false;
      }
      await self.getCurrentUser();
      return true;
    },
    changePassword: async (password: string) => {
      const result = await self.environment.api.changeUserPassword(password);
      if (result.kind !== "ok") {
        return false;
      }
      self.setPasswordSetupRequired(false);
      return true;
    },
    confirmEmail: async (token: string) => {
      const result = await self.environment.api.confirmEmail(token);

      if (result.kind === "ok") {
        const { data } = result;
        self.user?.setIsEmailConfirmed(data.isEmailConfirmed);
      }
    },
  }))
  .actions((self) => ({
    registerGovoriru: async (
      props1: RegisterProps,
      props2?: RegisterSecondGovoriruProps
    ) => {
      const result1 = await self.register(props1);

      if (result1 && props2) {
        await self.environment.api.registerSecondGovoriru(props2);
      }

      return result1;
    },
  }))
  .views((self) => ({
    get isSingIn(): boolean {
      return Boolean(self.user);
    },
  }));

type AuthStoreType = Instance<typeof AuthStoreModel>;
export type AuthStore = AuthStoreType;
type AuthStoreSnapshotType = SnapshotOut<typeof AuthStoreModel>;
export type AuthStoreSnapshot = AuthStoreSnapshotType;
export const createAuthStoreDefaultModel = (): unknown =>
  types.optional(AuthStoreModel, {} as any);
